'use client';

import { Utils, Random } from '@bsv/sdk';
import { getWallet } from '@/lib/wallet';

// BRC-42/43 "anyone-can-verify" signing protocol. Must match
// lib/api-auth-server.ts exactly (protocol name is lowercased into the
// invoice number, so keep it lowercase here).
export const API_AUTH_PROTOCOL_ID: [1, string] = [1, 'pelf api'];

// Wire envelope POSTed to signed endpoints. The client signs the UTF-8 bytes
// of `payloadJson` (the exact string sent — no re-serialization on either
// side, which sidesteps JSON canonicalization bugs). `signature` is the
// DER-encoded ECDSA signature as a byte array.
export type SignedRequestEnvelope = {
  payloadJson: string;
  signature: number[];
};

// Shape of the JSON object inside payloadJson.
export type SignedPayload = Record<string, unknown> & {
  pubkey: string; // signer identity key (compressed hex)
  ts: number; // Date.now() at signing — server enforces a 5-minute window
  nonce: string; // 16 random bytes, base64 — doubles as the BRC-43 keyID
};

/**
 * POST `body` to `endpoint` with a BRC-100 wallet signature binding the
 * request to `identityKey`. The wallet signs with counterparty 'anyone' so
 * the server can verify with ProtoWallet('anyone') without any shared state.
 */
export async function signedFetch(
  endpoint: string,
  body: Record<string, unknown>,
  identityKey: string
): Promise<Response> {
  const nonce = Utils.toBase64(Random(16));
  const payload: SignedPayload = {
    ...body,
    pubkey: identityKey,
    ts: Date.now(),
    nonce,
  };
  const payloadJson = JSON.stringify(payload);

  const { signature } = await getWallet().createSignature({
    data: Utils.toArray(payloadJson, 'utf8'),
    protocolID: API_AUTH_PROTOCOL_ID,
    keyID: nonce,
    counterparty: 'anyone',
  });

  const envelope: SignedRequestEnvelope = {
    payloadJson,
    signature: Array.from(signature),
  };

  return fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(envelope),
  });
}
